
import * as React from 'react';
import { IAvailabilityV3BookingsResponse } from '../../../api/bookings';
import { IBookingV3 } from '../../../data/entities/event/booking';
import { IProperty } from '../../../data/entities/property';
import { currencySymbolForCurrencyString } from '../../helpers/currency_helpers';
import { durationInNights, isoDateToUtcDateString } from '../../helpers/date_helpers';
import { calculateDiscrepancyRate } from '../../pages/property/bookings/details/helpers';
import { BookingNavTile } from '../booking-nav-tile/BookingNavTile';
import { HelpLink } from '../help-link/HelpLink';
import { Loader } from '../loader/Loader';
import { Pagination } from '../pagination/Pagination';
import { Paragraph } from '../paragraph/Paragraph';
import { Tag } from '../tag/Tag';
import { getBookingStatusDisplay, getTotalPax } from './helpers';

interface IBookingListProps {
	bookings: IAvailabilityV3BookingsResponse | undefined;
	properties?: IProperty[];
	loading: boolean;
	disablePagination?: boolean;
	paginate?(newPage: number): void;
}

const emptyState = <div className="booking-list__empty">
	<div className="booking-list__empty--title">There are no bookings to show.</div>
	<Paragraph>Our team can help improve your listing to attract more guests.</Paragraph>
	<HelpLink to="https://travelnest.freshdesk.com/support/solutions/folders/101000273136">
		Learn how to make your property more attractive to guests
	</HelpLink>
	<Paragraph><a href="mailto:owners@travelnest.com" target="_blank">Contact Travelnest for help</a></Paragraph>
</div>;

export const BookingList: React.FC<IBookingListProps> = (props) => {
	const { bookings, disablePagination, paginate } = props;

	return (
		<div className="booking-list">
			{props.loading && <Loader message="Your bookings are loading." loading />}
			{
				!props.loading && bookings?.data && bookings?.data.length === 0
					? emptyState
					: bookings?.data.map((b) => {
						const booking = b.booking || {} as IBookingV3;
						const guest = b.booking ? b.booking.guest : null;
						const guestName = guest && guest.name ? guest.name : '';
						const property = (props.properties || [])
							.find((x) => x.id.toString() === b.property_id || x.legacy_property_id === b.property_id);
						const currencySymbol = booking ? currencySymbolForCurrencyString(booking.booking_value?.currency) : '#';
						const rentValue = (booking.booking_value?.owner_rent_value || 0).toFixed(2);
						const bookingDisplayStatus = getBookingStatusDisplay(b);
						const acceptableDiscrepancyPercent = 5;
						const showOwnerRent =
							!(Math.abs(calculateDiscrepancyRate(
								booking.booking_value?.tn_booking_discrepancy,
								booking.booking_value?.tbv
							) * 100) > acceptableDiscrepancyPercent)
							&& booking.booking_value?.tn_commission_rate >= 0
							&& booking.booking_value?.owner_rent_value >= 0
							&& bookingDisplayStatus === 'Booked';
						const isCancelled = b.status === 'cancelled';
						const guestCount = getTotalPax(guest);
						return (
							<BookingNavTile
								to={`/property/${b.property_id}/booking/${b.id}`}
								type={isCancelled ? 'struck' : 'default'}
							>
								<section className={'booking-list-nav-tile'}>
									<div className={[
										'booking-list-nav-tile__column',
										...(isCancelled ? ['booking-list-nav-tile__column--struck'] : [])
									].join(' ')}>
										<div className="booking-list-nav-tile__guest-name">{guestName}</div>
										<div>{guestCount} {guestCount === 1 ? 'guest' : 'guests'}</div>
									</div>
									<div className="booking-list-nav-tile__column">
										<div>{property ? property.internal_name : ''}</div>
										<div>
											Reference: <span className="booking-list-nav-tile__text-highlight">{b.id}</span>
										</div>
									</div>
									<div className="booking-list-nav-tile__column">
										<div>{isoDateToUtcDateString(b.start_on)}</div>
										<div>
											Staying for <span className="booking-list-nav-tile__text-highlight">{durationInNights(b)} nights</span>
										</div>
									</div>

									<div className="booking-list-nav-tile__column">
										<div>{showOwnerRent ? currencySymbol + rentValue : null}</div>
									</div>

									<div className="booking-list-nav-tile__column">
										<div>
											<Tag status={isCancelled ? 'warning' : 'neutral'}>
												{bookingDisplayStatus}
											</Tag>
										</div>
									</div>
								</section>
							</BookingNavTile>
						);
					})}
			{
				bookings &&
				<Pagination
					onChange={paginate ? (newPage) => paginate(newPage) : () => false}
					size={bookings?._stats.page_size}
					current={bookings?._stats.page_number}
					length={bookings?._stats.total}
					disable={disablePagination}
				/>
			}

		</div>
	);
};
